import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, Grid, Divider, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem, InputLabel, FormControl, Fab, List, ListItem, ListItemText, Chip } from '@mui/material';
import { ref, get, remove, set } from 'firebase/database';
import { database, DB_PATHS } from '../config/firebase';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useSelector } from 'react-redux';
import { RootState } from '../types/store';
import { useDispatch } from 'react-redux';
import { setFilters } from '../store/slices/driverSlice';
import ChungcheongDepositManager from '../components/ChungcheongDepositManager';
import ChungcheongManagerRegistration from '../components/ChungcheongManagerRegistration';
import ChungcheongManagerEdit from '../components/ChungcheongManagerEdit';
import AutocallDepositManager from '../components/AutocallDepositManager';
import { getDisplayAppName } from '../utils/appNameUtils';
import { formatPhoneNumber } from '../utils/phoneNumberUtils';

interface Manager {
  phonenumber: string;
  username: string;
  password: string;
  grade: string;
  appname: string;
  block: string;
  initdate: string;
  referralcode: string;
}

interface DepositData {
  phonenumber: string;
  username: string;
  currentDeposit: number;
  lastUpdateDate: string;
}

interface DepositLog {
  timestamp: string;
  type: 'deposit' | 'withdraw';
  amount: number;
  date: string;
  reason?: string; // 출금 시 사유
  balance?: number; // 해당 시점의 잔액
}

interface NewManager {
  phonenumber: string;
  username: string;
  password: string;
  grade: ManagerGrade;
  appname: string;
  block: string;
  referralcode: string;
}

type ManagerGrade = 'supermaster' | 'localmaster' | 'localmanager';

const AdminPage: React.FC = () => {
  const [managers, setManagers] = useState<Manager[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<Manager | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newManager, setNewManager] = useState<NewManager>({
    phonenumber: '',
    username: '',
    password: '',
    grade: 'localmanager',
    appname: '',
    block: 'false',
    referralcode: '',
  });
  const [appList, setAppList] = useState<string[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editManager, setEditManager] = useState<Manager | null>(null);
  
  // 예치금 관련 상태
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState<Manager | null>(null);
  const [depositData, setDepositData] = useState<DepositData | null>(null);
  const [depositLogs, setDepositLogs] = useState<DepositLog[]>([]);
  const [depositAmount, setDepositAmount] = useState<string>('');

  // 앱 이름 필터 상태
  const [appFilter, setAppFilter] = useState<string>('');

  // 충청 예치금 관리 관련 상태
  const [chungcheongDepositDialogOpen, setChungcheongDepositDialogOpen] = useState(false);
  const [selectedChungcheongManager, setSelectedChungcheongManager] = useState<Manager | null>(null);

  // 충청 신규 관리자 등록 관련 상태
  const [chungcheongRegistrationDialogOpen, setChungcheongRegistrationDialogOpen] = useState(false);

  // 충청 관리자 수정 관련 상태
  const [chungcheongEditDialogOpen, setChungcheongEditDialogOpen] = useState(false);
  const [selectedChungcheongEditManager, setSelectedChungcheongEditManager] = useState<Manager | null>(null);

  // 오토콜 예치금 관리 관련 상태
  const [autocallDepositDialogOpen, setAutocallDepositDialogOpen] = useState(false);
  const [selectedAutocallManager, setSelectedAutocallManager] = useState<Manager | null>(null);

  const authUser = useSelector((state: RootState) => state.auth.user);
  const currentUser = authUser;
  const userGrade = authUser?.grade;
  const userAppname = authUser?.appname;
  const userReferralcode = authUser?.referralcode;

  const dispatch = useDispatch();

  if (userGrade === 'localmanager') return null;

  let filteredManagers = managers;
  if (userGrade === 'localmaster' && userAppname) {
    filteredManagers = managers.filter(m => m.appname === userAppname);
  }

  // 앱 이름 필터 적용
  if (appFilter && appFilter !== '') {
    filteredManagers = filteredManagers.filter(m => m.appname === appFilter);
  }

  if (userGrade === 'localmanager' && userAppname && userReferralcode) {
    dispatch(setFilters({ region: userAppname, referealcode: userReferralcode }));
  }

  const gradeOrder: Record<string, number> = {
    supermaster: 0,
    localmaster: 1,
    localmanager: 2,
  };
  const sortedManagers = [...filteredManagers].sort((a, b) => {
    const gradeDiff = (gradeOrder[a.grade] ?? 99) - (gradeOrder[b.grade] ?? 99);
    if (gradeDiff !== 0) return gradeDiff;
    return a.username.localeCompare(b.username);
  });

  const fetchManagers = async () => {
    const snapshot = await get(ref(database, DB_PATHS.MANAGER));
    const data = snapshot.val();
    if (data) {
      setManagers(Object.values(data));
    }
  };

  useEffect(() => {
    fetchManagers();
  }, []);

  useEffect(() => {
    const fetchAppList = async () => {
      const snapshot = await get(ref(database, DB_PATHS.DRIVERS));
      const data = snapshot.val();
      if (data) {
        const apps = Array.from(new Set(Object.values(data).map((d: any) => d.appname).filter(Boolean)));
        setAppList(apps);
      }
    };
    fetchAppList();
  }, []);

  const handleEditClick = (manager: Manager) => {
    // 충청 앱의 localmanager인 경우 별도 컴포넌트 사용
    if (manager.appname === 'smlink_M2_충청' && manager.grade === 'localmanager') {
      setSelectedChungcheongEditManager(manager);
      setChungcheongEditDialogOpen(true);
    } else {
      // 기존 로직 사용
      setEditManager({ ...manager });
      setEditDialogOpen(true);
    }
  };

  const handleEditSave = async () => {
    if (!editManager) return;
    await set(ref(database, `${DB_PATHS.MANAGER}/${editManager.phonenumber}`), editManager);
    setManagers(prev => prev.map(m => m.phonenumber === editManager.phonenumber ? editManager : m));
    setEditDialogOpen(false);
    setEditManager(null);
  };

  const handleChungcheongEditSave = (updatedManager: Manager) => {
    setManagers(prev => prev.map(m => m.phonenumber === updatedManager.phonenumber ? updatedManager : m));
    setChungcheongEditDialogOpen(false);
    setSelectedChungcheongEditManager(null);
  };

  // 예치금 관련 함수들
  const handleDepositClick = async (manager: Manager) => {
    setSelectedManager(manager);
    await fetchDepositData(manager.phonenumber);
    setDepositDialogOpen(true);
  };

  const fetchDepositData = async (phonenumber: string) => {
    try {
      // deposit 테이블에서 데이터 가져오기
      const depositRef = ref(database, `deposit/${phonenumber}`);
      const depositSnapshot = await get(depositRef);
      
      if (depositSnapshot.exists()) {
        const data = depositSnapshot.val();
        setDepositData(data);
      } else {
        // 데이터가 없는 경우 0으로 초기화
        setDepositData({
          phonenumber: phonenumber,
          username: selectedManager?.username || '',
          currentDeposit: 0,
          lastUpdateDate: ''
        });
      }

      // depositlogs 테이블에서 로그 가져오기
      const logsRef = ref(database, `depositlogs/${phonenumber}`);
      const logsSnapshot = await get(logsRef);
      
      if (logsSnapshot.exists()) {
        const logsData = logsSnapshot.val();
        const logs = Object.entries(logsData).map(([timestamp, logData]: [string, any]) => ({
          timestamp,
          type: logData.type,
          amount: logData.amount,
          date: logData.date,
          reason: logData.reason || '', // reason 필드 추가
          balance: logData.balance || 0 // balance 필드 추가
        })).sort((a, b) => b.timestamp.localeCompare(a.timestamp)); // 최신순 정렬
        setDepositLogs(logs);
      } else {
        setDepositLogs([]);
      }
    } catch (error) {
      console.error('예치금 데이터 조회 실패:', error);
      // 오류 발생 시에도 0으로 초기화
      setDepositData({
        phonenumber: phonenumber,
        username: selectedManager?.username || '',
        currentDeposit: 0,
        lastUpdateDate: ''
      });
      setDepositLogs([]);
    }
  };

  const handleDepositSave = async () => {
    if (!selectedManager || !depositAmount || isNaN(Number(depositAmount))) {
      alert('올바른 금액을 입력하세요.');
      return;
    }

    // 입력값을 정수로 변환
    const amount = Math.floor(Number(depositAmount));
    if (amount <= 0) {
      alert('0보다 큰 금액을 입력하세요.');
      return;
    }

    try {
      const now = new Date();
      const timestamp = now.getFullYear().toString() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0') +
        String(now.getSeconds()).padStart(2, '0');

      const dateString = now.getFullYear() + '-' +
        String(now.getMonth() + 1).padStart(2, '0') + '-' +
        String(now.getDate()).padStart(2, '0') + ' ' +
        String(now.getHours()).padStart(2, '0') + ':' +
        String(now.getMinutes()).padStart(2, '0') + ':' +
        String(now.getSeconds()).padStart(2, '0');

      const currentDeposit = Math.floor(depositData?.currentDeposit || 0);
      const newDeposit = Math.floor(currentDeposit + amount);

      // deposit 테이블 업데이트
      const depositUpdateData = {
        phonenumber: selectedManager.phonenumber,
        username: selectedManager.username,
        currentDeposit: newDeposit,
        lastUpdateDate: dateString
      };

      await set(ref(database, `deposit/${selectedManager.phonenumber}`), depositUpdateData);

      // depositlogs 테이블에 로그 추가
      const logData = {
        type: 'deposit',
        amount: amount,
        date: dateString,
        balance: newDeposit // 입금 후 잔액 추가
      };

      await set(ref(database, `depositlogs/${selectedManager.phonenumber}/${timestamp}`), logData);

      // 상태 업데이트
      setDepositData(depositUpdateData);
      setDepositLogs(prev => [{
        timestamp,
        type: 'deposit',
        amount,
        date: dateString,
        balance: newDeposit // 입금 후 잔액 추가
      }, ...prev]);

      setDepositAmount('');
      alert('예치금이 성공적으로 입금되었습니다.');
    } catch (error) {
      console.error('예치금 저장 실패:', error);
      alert('예치금 저장에 실패했습니다.');
    }
  };

  const handleDepositDialogClose = () => {
    setDepositDialogOpen(false);
    setSelectedManager(null);
    setDepositData(null);
    setDepositLogs([]);
    setDepositAmount('');
  };

  const handleOpenAddDialog = () => {
    if (currentUser && (currentUser.grade === 'supermaster' || currentUser.grade === 'localmaster')) {
      // 충청 앱인 경우 별도 컴포넌트 사용
      if (currentUser.appname === 'smlink_M2_충청') {
        setChungcheongRegistrationDialogOpen(true);
      } else {
        // 기존 로직 사용
        setAddDialogOpen(true);
      }
    }
  };

  // addDialogOpen 상태가 변경될 때 newManager 초기화
  useEffect(() => {
    if (addDialogOpen && currentUser) {
      const initialGrade = currentUser.grade === 'supermaster' ? 'localmaster' : 'localmanager';
      console.log('다이얼로그 열기 - 초기 grade:', initialGrade);
      setNewManager({
        phonenumber: '',
        username: '',
        password: '',
        grade: initialGrade as ManagerGrade,
        appname: currentUser.grade === 'supermaster' ? '' : currentUser.appname || '',
        block: 'false',
        referralcode: '',
      });
    }
  }, [addDialogOpen, currentUser]);

  // 다이얼로그 닫기 처리
  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    // 다이얼로그가 닫힐 때 newManager 초기화
    setNewManager({
      phonenumber: '',
      username: '',
      password: '',
      grade: 'localmanager',
      appname: '',
      block: 'false',
      referralcode: '',
    });
  };



  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        관리자 목록
      </Typography>
      
      {/* 앱 이름 필터 */}
      {userGrade === 'supermaster' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            앱 이름 필터
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label="전체"
              onClick={() => setAppFilter('')}
              color={appFilter === '' ? 'primary' : 'default'}
              variant={appFilter === '' ? 'filled' : 'outlined'}
              size="small"
            />
            {appList.map((app) => {
              const displayName = getDisplayAppName(app);
              return (
                <Chip
                  key={app}
                  label={displayName}
                  onClick={() => setAppFilter(app)}
                  color={appFilter === app ? 'primary' : 'default'}
                  variant={appFilter === app ? 'filled' : 'outlined'}
                  size="small"
                />
              );
            })}
          </Box>
        </Box>
      )}

      <Grid container spacing={2}>
        {sortedManagers.map((manager, index) => (
          <Grid item xs={12} sm={6} md={4} key={manager.phonenumber}>
            <Card sx={{ mb: 2, borderRadius: 3, boxShadow: 3, bgcolor: '#fff' }}>
              <CardContent sx={{ position: 'relative' }}>
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', top: 0, right: 72 }}
                  onClick={() => handleEditClick(manager)}
                >
                  <EditIcon color="primary" />
                </IconButton>
                {/* 예치금 버튼 - supermaster이고 localmaster이며 smlink_M2_오토콜인 경우만 표시 */}
                {userGrade === 'supermaster' && manager.grade === 'localmaster' && (manager.appname === 'smlink_M2_오토콜' || manager.appname === 'smlink_M2_이지캐치') && (
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 0, right: 36 }}
                    onClick={() => handleDepositClick(manager)}
                  >
                    <AccountBalanceWalletIcon color="success" />
                  </IconButton>
                )}
                {/* localmaster가 자기 자신의 예치금을 관리할 수 있는 버튼 */}
                {userGrade === 'localmaster' && manager.grade === 'localmaster' && 
                 manager.phonenumber === currentUser?.id && 
                 (manager.appname === 'smlink_M2_오토콜' || manager.appname === 'smlink_M2_이지캐치') && (
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 0, right: 36 }}
                    onClick={() => handleDepositClick(manager)}
                  >
                    <AccountBalanceWalletIcon color="success" />
                  </IconButton>
                )}
                {/* 충청 예치금 버튼 - supermaster이고 localmanager이며 smlink_M2_충청인 경우만 표시 */}
                {userGrade === 'supermaster' && manager.grade === 'localmanager' && manager.appname === 'smlink_M2_충청' && (
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 0, right: 36 }}
                    onClick={() => {
                      setSelectedChungcheongManager(manager);
                      setChungcheongDepositDialogOpen(true);
                    }}
                  >
                    <AccountBalanceWalletIcon color="success" />
                  </IconButton>
                )}
                {/* localmaster로 로그인했을 때 충청 앱의 localmanager 예치금 관리 아이콘 표시 */}
                {userGrade === 'localmaster' && userAppname === 'smlink_M2_충청' && manager.grade === 'localmanager' && manager.appname === 'smlink_M2_충청' && (
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 0, right: 36 }}
                    onClick={() => {
                      setSelectedChungcheongManager(manager);
                      setChungcheongDepositDialogOpen(true);
                    }}
                  >
                    <AccountBalanceWalletIcon color="success" />
                  </IconButton>
                )}
                {/* 오토콜 앱의 localmanager 예치금 관리 아이콘 표시 */}
                {manager.grade === 'localmanager' && manager.appname === 'smlink_M2_오토콜' && (
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 0, right: 36 }}
                    onClick={() => {
                      setSelectedAutocallManager(manager);
                      setAutocallDepositDialogOpen(true);
                    }}
                  >
                    <AccountBalanceWalletIcon color="success" />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', top: 0, right: 0 }}
                  onClick={() => {
                    setDeleteTarget(manager);
                    setDialogOpen(true);
                  }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {`${index + 1}) ${manager.username}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  전화번호: {formatPhoneNumber(manager.phonenumber)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  비밀번호: {manager.password}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  권한: {manager.grade}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  앱: {getDisplayAppName(manager.appname)}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">차단: {manager.block}</Typography>
                <Typography variant="body2">가입일: {manager.initdate}</Typography>
                <Typography variant="body2">추천코드: {manager.referralcode}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* 기존 다이얼로그들 */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>관리자 삭제</DialogTitle>
        <DialogContent>
          정말로 {deleteTarget?.username} 관리자를 삭제하시겠습니까?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button color="error" onClick={async () => {
            if (deleteTarget) {
              await remove(ref(database, `${DB_PATHS.MANAGER}/${deleteTarget.phonenumber}`));
              setManagers((prev) => prev.filter((m) => m.phonenumber !== deleteTarget.phonenumber));
              setDialogOpen(false);
              setDeleteTarget(null);
            }
          }}>삭제</Button>
        </DialogActions>
      </Dialog>

      {/* 예치금 다이얼로그 */}
      <Dialog open={depositDialogOpen} onClose={handleDepositDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>예치금 관리</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              전화번호: {formatPhoneNumber(selectedManager?.phonenumber || '')}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              이름: {selectedManager?.username}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              현재 예치금: {depositData?.currentDeposit?.toLocaleString() || '0'}원
            </Typography>
            {depositData?.lastUpdateDate && (
              <Typography variant="body2" color="text.secondary">
                마지막 업데이트: {depositData.lastUpdateDate}
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              label="예치금 입금"
              type="number"
              value={depositAmount}
              onChange={(e) => {
                // 입력값을 정수로 변환
                const value = e.target.value;
                const intValue = value ? Math.floor(Number(value)) : '';
                setDepositAmount(intValue.toString());
              }}
              fullWidth
              margin="normal"
              placeholder="입금할 금액을 입력하세요"
              inputProps={{
                step: 1, // 정수 단위로만 입력 가능
                min: 1  // 최소값 1
              }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              예치금 히스토리
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
              {depositLogs.length > 0 ? (
                <List dense>
                  {depositLogs.map((log, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={
                          <Box>
                            {/* 모바일 뷰 - 세로 배치 */}
                            <Box sx={{ 
                              display: { xs: 'flex', md: 'none' }, 
                              flexDirection: 'column',
                              gap: 1
                            }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">{log.date}</Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: log.type === 'deposit' ? '#4caf50' : '#f44336',
                                    fontWeight: 600
                                  }}
                                >
                                  {log.type === 'deposit' ? '+' : '-'}{log.amount.toLocaleString()}원
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: log.type === 'deposit' ? '#4caf50' : '#f44336'
                                  }}
                                >
                                  {log.type === 'deposit' ? '입금' : '출금'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  잔액: {(log.balance ?? 0).toLocaleString()}원
                                </Typography>
                              </Box>
                            </Box>

                            {/* PC 뷰 - 가로 배치 */}
                            <Box sx={{ 
                              display: { xs: 'none', md: 'flex' }, 
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <Box sx={{ width: '140px' }}>{log.date}</Box>
                              <Box sx={{ 
                                width: '60px', 
                                textAlign: 'center', 
                                color: log.type === 'deposit' ? '#4caf50' : '#f44336'
                              }}>
                                {log.type === 'deposit' ? '입금' : '출금'}
                              </Box>
                              <Box sx={{ 
                                width: '120px', 
                                textAlign: 'right', 
                                fontWeight: 600,
                                color: log.type === 'deposit' ? '#4caf50' : '#f44336'
                              }}>
                                {log.type === 'deposit' ? '+' : '-'}{log.amount.toLocaleString()}원
                              </Box>
                              <Divider orientation="vertical" flexItem sx={{ mx: 2, display: { xs: 'none', md: 'block' } }} />
                              <Box sx={{ width: '120px', textAlign: 'right', color: 'text.secondary' }}>
                                잔액: {(log.balance ?? 0).toLocaleString()}원
                              </Box>
                            </Box>
                          </Box>
                        }
                        secondary={
                          log.type === 'withdraw' && log.reason ? (
                            <Box sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.875rem' }}>
                              사유: {log.reason}
                            </Box>
                          ) : null
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                  히스토리가 없습니다.
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDepositDialogClose}>닫기</Button>
          <Button onClick={handleDepositSave} variant="contained" disabled={!depositAmount || isNaN(Number(depositAmount))}>
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 충청 예치금 관리 다이얼로그 */}
      <ChungcheongDepositManager
        open={chungcheongDepositDialogOpen}
        onClose={() => {
          setChungcheongDepositDialogOpen(false);
          setSelectedChungcheongManager(null);
        }}
        selectedManager={selectedChungcheongManager}
        currentUser={currentUser}
      />

      {/* 충청 신규 관리자 등록 다이얼로그 */}
      <ChungcheongManagerRegistration
        open={chungcheongRegistrationDialogOpen}
        onClose={() => setChungcheongRegistrationDialogOpen(false)}
        currentUser={currentUser}
        onManagerAdded={fetchManagers}
      />

      {/* 충청 관리자 수정 다이얼로그 */}
      <ChungcheongManagerEdit
        open={chungcheongEditDialogOpen}
        onClose={() => {
          setChungcheongEditDialogOpen(false);
          setSelectedChungcheongEditManager(null);
        }}
        manager={selectedChungcheongEditManager}
        onSave={handleChungcheongEditSave}
      />

      {/* 오토콜 예치금 관리 다이얼로그 */}
      <AutocallDepositManager
        open={autocallDepositDialogOpen}
        onClose={() => {
          setAutocallDepositDialogOpen(false);
          setSelectedAutocallManager(null);
        }}
        selectedManager={selectedAutocallManager}
        currentUser={currentUser}
      />

      <Box sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1000 }}>
        <Fab color="primary" onClick={handleOpenAddDialog}>
          <AddIcon />
        </Fab>
      </Box>

      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog}>
        <DialogTitle>신규 관리자 등록</DialogTitle>
        <DialogContent>
          <TextField
            label="전화번호"
            value={newManager.phonenumber}
            onChange={e => setNewManager({ ...newManager, phonenumber: e.target.value })}
            fullWidth margin="dense"
          />
          <TextField
            label="이름"
            value={newManager.username}
            onChange={e => setNewManager({ ...newManager, username: e.target.value })}
            fullWidth margin="dense"
          />
          <TextField
            label="비밀번호"
            type="password"
            value={newManager.password}
            onChange={e => setNewManager({ ...newManager, password: e.target.value })}
            fullWidth margin="dense"
          />
          <FormControl fullWidth margin="dense">
            <TextField
              label="권한"
              value={currentUser?.grade === 'supermaster' ? 'localmaster' : 'localmanager'}
              InputProps={{
                readOnly: true,
              }}
              fullWidth
              margin="dense"
            />
          </FormControl>
          <FormControl fullWidth margin="dense">
            {currentUser?.grade === 'supermaster' && (
            <InputLabel>앱(지역)</InputLabel>
            )}
            {currentUser?.grade === 'supermaster' ? (
            <Select
              value={newManager.appname}
              label="앱(지역)"
              onChange={e => setNewManager({ ...newManager, appname: e.target.value })}
            >
              {appList.map(app => (
                <MenuItem key={app} value={app}>{getDisplayAppName(app)}</MenuItem>
              ))}
            </Select>
            ) : (
              <TextField
                label="앱(지역)"
                value={currentUser?.appname || ''}
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
                margin="dense"
              />
            )}
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>차단</InputLabel>
            <Select
              value={newManager.block}
              label="차단"
              onChange={e => setNewManager({ ...newManager, block: e.target.value })}
            >
              <MenuItem value="false">false</MenuItem>
              <MenuItem value="true">true</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="추천코드"
            value={newManager.referralcode}
            onChange={e => setNewManager({ ...newManager, referralcode: e.target.value })}
            fullWidth margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>취소</Button>
          <Button
            onClick={async () => {
              if (!newManager.phonenumber || !newManager.username || !newManager.password || !newManager.appname) {
                alert('필수 정보를 모두 입력하세요.');
                return;
              }
              const today = dayjs().format('YYYY-MM-DD');
              const managerData = {
                ...newManager,
                initdate: today,
              };
              await set(ref(database, `${DB_PATHS.MANAGER}/${newManager.phonenumber}`), managerData);
              setManagers(prev => [...prev, managerData]);
              handleCloseAddDialog();
            }}
            variant="contained"
          >
            저장
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>관리자 정보 수정</DialogTitle>
        <DialogContent>
          <TextField
            label="전화번호"
            value={editManager?.phonenumber || ''}
            onChange={e => setEditManager(editManager ? { ...editManager, phonenumber: e.target.value } : null)}
            fullWidth margin="dense"
            disabled
          />
          <TextField
            label="이름"
            value={editManager?.username || ''}
            onChange={e => setEditManager(editManager ? { ...editManager, username: e.target.value } : null)}
            fullWidth margin="dense"
          />
          <TextField
            label="비밀번호"
            type="text"
            value={editManager?.password || ''}
            onChange={e => setEditManager(editManager ? { ...editManager, password: e.target.value } : null)}
            fullWidth margin="dense"
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>권한</InputLabel>
            <Select
              value={editManager?.grade || ''}
              label="권한"
              onChange={e => setEditManager(editManager ? { ...editManager, grade: e.target.value } : null)}
            >
              <MenuItem value="localmaster">localmaster</MenuItem>
              <MenuItem value="localmanager">localmanager</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>차단</InputLabel>
            <Select
              value={editManager?.block || ''}
              label="차단"
              onChange={e => setEditManager(editManager ? { ...editManager, block: e.target.value } : null)}
            >
              <MenuItem value="false">false</MenuItem>
              <MenuItem value="true">true</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="추천코드"
            value={editManager?.referralcode || ''}
            onChange={e => setEditManager(editManager ? { ...editManager, referralcode: e.target.value } : null)}
            fullWidth margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>취소</Button>
          <Button onClick={handleEditSave} variant="contained">저장</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPage; 